import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  setPastDueSTOCheck,
  setPastDueStoFilter,
} from "../../../../store/Actions";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((_theme) => ({
  chartContainer: {
    position: "relative",
    overflow: "hidden",
  },
  sliderContainer: {
    margin: "20px 0",
    padding: "0 30px",
    position: "relative",
    maxWidth: "600px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  sliderWrapper: {
    position: "relative",
    height: "8px",
    display: "flex",
    alignItems: "center",
  },
  slider: {
    WebkitAppearance: "none",
    width: "100%",
    height: "4px",
    background: "#e0e0e0",
    borderRadius: "2px",
    outline: "none",
    "&::-webkit-slider-thumb": {
      WebkitAppearance: "none",
      width: "20px",
      height: "20px",
      background: "#0067A1",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        transform: "scale(1.1)",
        background: "#004d80",
      },
      "&:active": {
        transform: "scale(1)",
      },
    },
  },
  pageIndicator: {
    position: "absolute",
    right: "0",
    bottom: "-24px",
    color: "#666",
    fontSize: "0.8rem",
    fontWeight: 400,
    fontFamily: "system-ui, sans-serif",
    "& span": {
      color: "#0067A1",
      fontWeight: 500,
    },
  },
  progressTrack: {
    position: "absolute",
    height: "4px",
    background: "#0067A1",
    borderRadius: "2px",
    transition: "width 0.3s ease",
  },
}));

const DoBarChart = ({
  _chartData = [],
  _categoryList = [],
  showAnnotation = false,
  yAxisTitle = "Values",
  graphData = [],
  activeTab = "Recommendation",
}) => {
  const [borderRadius, setBorderRadius] = useState(10);
  // const [isStacked, setIsStacked] = useState(false);
  const isStacked = false;
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5;
  const dispatch = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    setCurrentPage(0);
  }, [activeTab]);

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 1000) {
        setBorderRadius(5);
      } else if (screenWidth < 1500) {
        setBorderRadius(6);
      } else {
        setBorderRadius(10);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const safeGraphData = Array.isArray(graphData) ? graphData : [];

  const calculateColumnWidth = () => {
    const categoriesLength = safeGraphData.length;
    if (categoriesLength === 1) {
      return "2%";
    } else if (categoriesLength === 2) {
      return "5%";
    } else if (categoriesLength === 3) {
      return "8%";
    } else {
      return "10%";
    }
  };

  const determineSeriesData = (item) => {
    switch (activeTab) {
      case "STRs Required":
        return item.Recommended_STR || 0;
      case "TLs This Month":
        return item.Recommended_TL_Today || 0;
      case "STRs To Cancel":
        return item.Cancelled_STR || 0;
      case "Past Due STOs":
        return item.Past_due_STO || 0;
      case "Transport Spend by Lane":
        return item.TransportCost || 0;
      default:
        return 0;
    }
  };

  const seriesData = safeGraphData.map(determineSeriesData);
  const xAxisCategories = safeGraphData.map(
    (item) => item.Metric_name || item.LaneID || "Unknown Metric"
  );

  // Pagination logic
  const totalItems = safeGraphData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const slicedCategories = xAxisCategories.slice(startIndex, endIndex);
  const slicedSeriesData = [
    {
      name: yAxisTitle,
      data: seriesData.slice(startIndex, endIndex),
    },
  ];

  const formatTransportCost = (value) => {
    if (value >= 1_000_000) {
      return (value / 1_000_000).toFixed(1) + "M";
    } else if (value >= 1_000) {
      return (value / 1_000).toFixed(1) + "K";
    }
    return value;
  };

  const options = {
    chart: {
      type: "bar",
      height: 350,
      stacked: isStacked,
      toolbar: { show: false },
      zoom: { enabled: true, type: "x", autoScaleYaxis: true },
      events: {
        dataPointSelection: (_event, _chartContext, config) => {
          const clickedBarIndex = config.dataPointIndex;
          const clickedBarData = safeGraphData[clickedBarIndex];
          const apiData = {
            selectedOption: clickedBarData,
            PastDueFlag: true,
            tab: activeTab,
          };
          if (
            activeTab === "Past Due STOs" ||
            activeTab === "STRs To Cancel" ||
            activeTab === "STRs Required"
          ) {
            dispatch(setPastDueSTOCheck(apiData));
          }
        },
      },
    },
    fill: { colors: ["#EC6FBD"] },
    series: slicedSeriesData,
    yaxis: {
      labels: {
        formatter: (value) => formatTransportCost(value),
      },
      title: {
        text: yAxisTitle,
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          fontWeight: 500,
          color: "#333",
        },
      },
    },
    xaxis: {
      categories:
        slicedCategories.length > 0 ? slicedCategories : ["No Data Available"],
      tickAmount: slicedCategories.length,
      // tickPlacement: "on",
      labels: {
        maxHeight: 100,
        rotate: -45,
        show: true,
        trim: true,
        hideOverlappingLabels: false,
        showDuplicates: true,
        rotateAlways: true,
        style: {
          fontSize: "12px",
          fontFamily: "Poppins, sans-serif",
          transform: "rotate(-45deg) !important",
          transformOrigin: "top right !important",
          whiteSpace: "pre-line",
        },
      },
    },
    annotations: {
      yaxis: showAnnotation ? [] : [],
    },
    grid: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: calculateColumnWidth(),
        borderRadius: borderRadius,
        borderRadiusApplication: "end",
        borderColors: ["#000000"],
        borderWidth: 2,
      },
    },
    tooltip: {
      y: {
        formatter: (value) => formatTransportCost(value),
      },
    },
    noData: {
      text: "No Data Available",
      align: "center",
      verticalAlign: "middle",
      style: {
        color: "#404041",
        fontSize: "14px",
        fontFamily: "Poppins, sans-serif",
      },
    },
  };

  return (
    <div id="chart">
      <Chart
        options={options}
        series={slicedSeriesData}
        type="bar"
        height={300}
      />
      {totalPages > 1 &&
        (activeTab === "STRs To Cancel" || activeTab === "TLs This Month") && (
          <div className={classes.sliderContainer}>
            <div className={classes.sliderWrapper}>
              <div
                className={classes.progressTrack}
                style={{
                  width: `${((currentPage + 1) / totalPages) * 100}%`,
                  opacity: 0.3,
                }}
              />
              <input
                type="range"
                min="0"
                max={totalPages - 1}
                value={currentPage}
                onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
                className={classes.slider}
              />
            </div>
            <div className={classes.pageIndicator}>
              <span>{currentPage + 1}</span> / {totalPages}
            </div>
          </div>
        )}
    </div>
  );
};

export default DoBarChart;
